import React, { useEffect, useState } from "react";
import { Dropdown, message, Upload } from "antd";
import axios from "axios";
import uniqid from "uniqid";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../../../Core/CommonV2/CustomModal";
import CustomInputBox from "../../../../Core/CommonV2/CustomInputBox";
import ImageCropModal from "../../../../Core/CommonV3/ImageCropModal";
import CustomButton from "../../../../Core/CommonV2/CustomButton";
import { UploadIcon } from "../../../../Core/svgV3/UploadIcon";
import { DeleteIcon } from "../../../../Core/svgV3/DeleteIcon";
import { getThreads } from "../../../../Core/redux/api/conversationAPI";
import { editGroup } from "../../../../Core/redux/api/v3/conversationAPI";
import { updateGroupInfo } from "../../../../Core/redux/slices/conversations.slice";
import { useAppSelector } from "../../../../Core/redux/hooks";
import { useAppDispatch } from "../../../../Core/redux/hooks";
import { updateConversations } from "../../../../Core/redux/slices/conversations.slice";

const EditGroupModalFadeStyles = () => (
  <style>{`
    @keyframes waEditModalFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes waEditModalFadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }

    .wa-edit-group-modal .ant-zoom-enter,
    .wa-edit-group-modal .ant-zoom-appear {
      opacity: 0 !important;
      transform: none !important;
    }
    .wa-edit-group-modal .ant-zoom-enter.ant-zoom-enter-active,
    .wa-edit-group-modal .ant-zoom-appear.ant-zoom-appear-active {
      animation-name: waEditModalFadeIn !important;
      animation-duration: 0.2s !important;
      animation-timing-function: ease-out !important;
      transform: none !important;
    }
    .wa-edit-group-modal .ant-zoom-leave.ant-zoom-leave-active {
      animation-name: waEditModalFadeOut !important;
      animation-duration: 0.15s !important;
      animation-timing-function: ease-in !important;
    }
  `}</style>
);

const GroupImageMenuStyles = () => (
  <style>{`
    .wa-edit-group-image-menu.ant-dropdown {
      position: absolute !important;
      inset: auto !important;      /* nukes antd's bottomLeft placement inset first */
      top: 60% !important;
      left: 50% !important;
      right: auto !important;
      bottom: auto !important;
      /* -50% would dead-center the box on the image (symmetric overlap).
         Figma leans the menu right of center instead, so pull back less
         than half the box's own width. */
      transform: translate(-20%, 0) !important;
      z-index: 20 !important;
    }
  `}</style>
);

const GroupNameInputStyles = () => (
  <style>{`
    .wa-group-name-input.border-primary-500 {
      border-color: #7DC7FF !important;
    }
    .wa-group-name-input.shadow-input-focus {
      box-shadow: 0 0 0 2px #D1E9FF !important;
    }
    .wa-group-name-input.border-grey-200:hover {
      border-color: #7DC7FF !important;
    }
    .wa-group-name-input input {
      color: #292927 !important;
    }
  `}</style>
);

const CancelButtonStyles = () => (
  <style>{`
    .wa-cancel-btn,
    .wa-cancel-btn * {
      color: #667085 !important;
    }
  `}</style>
);

const EditGroupModal = ({
  visible,
  onCloseCallback,
  groupId,
  groupName = "",
  groupImage,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.userDetails);
  const { tenantDetails } = useAppSelector((state) => state.tenant);
  const { currentThread } = useAppSelector((state) => state.conversations);
  const [editedGroupName, setEditedGroupName] = useState(groupName ?? "");
  const [editedGroupImage, setEditedGroupImage] = useState(groupImage ?? "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Controls the "Upload Photo / Remove Photo" dropdown. It's a plain div
  // overlay (not an antd Menu), so antd won't auto-close it on item click —
  // we have to drive open/close ourselves.
  const [imageMenuOpen, setImageMenuOpen] = useState(false);

  // ── Prefill on OPEN, not on close ────────────────────────────────────────

  useEffect(() => {
    if (visible) {
      setEditedGroupName(groupName ?? "");
      setEditedGroupImage(groupImage ?? "");
    } else {
      setUploading(false);
      setSaving(false);
      setImageMenuOpen(false);
    }
  }, [visible, groupName, groupImage]);

  const handleImageChange = (info) => {
    // A file was picked (or the upload errored/finished) — either way the
    // dropdown's job is done, close it.
    setImageMenuOpen(false);

    if (info.file.status === "uploading") {
      setUploading(true);
      return;
    }
    setUploading(false);

    if (info.file.status === "done") {
      const imageUrl = info.file.response && info.file.response.url;
      if (imageUrl) {
        setEditedGroupImage(imageUrl);
      } else {
        message.error("Image upload failed. Please try again.");
      }
    } else if (info.file.status === "error") {
      message.error("Image upload failed. Please try again.");
    }
  };

  const uploadImage = async (options) => {
    const { onSuccess, onError, file, onProgress } = options;
    const fmData = new FormData();
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
      onUploadProgress: (event) => {
        onProgress({ percent: (event.loaded / event.total) * 100 });
      },
    };

    const data = new FormData();
    data.append(
      "file",
      file,
      uniqid(`${file.name.split(".")[0]}-`, `.${file.name.split(".").pop()}`)
    );

    const _file = data.get("file");
    fmData.append("image", _file);

    try {
      const res = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/v1/upload/storej",
        fmData,
        config
      );
      if (res.data && res.data.length > 0) {
        const imageUrl = res.data[0].fileUrl;
        onSuccess({ url: imageUrl });
      } else {
        onError({ message: "Image upload failed. Please try again." });
      }
    } catch (err) {
      onError({ err });
    }
  };

  const handleRemovePhoto = () => {
    setEditedGroupImage("");
    setImageMenuOpen(false);
  };

  const handleSave = async () => {
    const trimmedName = (editedGroupName ?? "").trim();
    if (!trimmedName) {
      message.error("Please provide a group name.");
      return;
    }
    if (saving || uploading) return;

    setSaving(true);

    const previousName = groupName;
    const previousImage = groupImage;
    const isCurrentlyOpenThread = String(currentThread?.threadId) === String(groupId);

    dispatch(
      updateGroupInfo({
        groupId,
        name: trimmedName,
        profilePicture: editedGroupImage || undefined,
      })
    );

    if (isCurrentlyOpenThread) {
      dispatch(
        updateConversations({
          key: "currentThread",
          value: {
            ...currentThread,
            name: trimmedName,
            groupProfilePicture: editedGroupImage || "",
          },
        })
      );
    }

    setSaving(false);
    onClose();

    try {
      const result = await editGroup({
        groupId,
        name: trimmedName,
        profilePicture: editedGroupImage || null,
      });

      if (!result) {
        dispatch(
          updateGroupInfo({
            groupId,
            name: previousName,
            profilePicture: previousImage || undefined,
          })
        );
        if (isCurrentlyOpenThread) {
          dispatch(
            updateConversations({
              key: "currentThread",
              value: {
                ...currentThread,
                name: previousName,
                groupProfilePicture: previousImage || "",
              },
            })
          );
        }
        message.error("Failed to update group. Please try again.");
        return;
      }

      dispatch(
        getThreads({ userId: user.id, tenantId: tenantDetails.tenantId })
      );
    } catch (error) {
      console.error("Error updating group:", error);
      dispatch(
        updateGroupInfo({
          groupId,
          name: previousName,
          profilePicture: previousImage || undefined,
        })
      );
      if (isCurrentlyOpenThread) {
        dispatch(
          updateConversations({
            key: "currentThread",
            value: {
              ...currentThread,
              name: previousName,
              groupProfilePicture: previousImage || "",
            },
          })
        );
      }
      message.error("Failed to update group. Please try again.");
    }
  };

  const onClose = () => {
    onCloseCallback();
  };

  const content = (
    <div
      className="flex flex-col w-[187px] rounded-lg border overflow-hidden px-1 py-1"
      style={{
        backgroundColor: "#F9FBFE",
        borderColor: "#EDF3F9",
        height: "88px",
        boxShadow:
          "0px 12px 16px -4px rgba(16,24,40,0.08), 0px 4px 6px -2px rgba(16,24,40,0.03)",
      }}
    >
      <div className="overflow-hidden shrink-0" style={{ maxHeight: 44 }}>
        <ImageCropModal cropShape={"round"}>
          <Upload
            action={process.env.REACT_APP_API_BASE_URL + "/v1/upload/storej"}
            customRequest={uploadImage}
            onChange={handleImageChange}
            multiple={false}
            showUploadList={false}
          >
            <div
              className="w-full flex items-center gap-2 px-3.5 py-2.5 cursor-pointer whitespace-nowrap"
              style={{ color: "#344054" }}
            >
              <UploadIcon />
              <span className="font-['Outfit'] font-normal text-sm">Upload Photo</span>
            </div>
          </Upload>
        </ImageCropModal>
      </div>
      <div
        onClick={handleRemovePhoto}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 cursor-pointer whitespace-nowrap"
        style={{ color: "#D92D20" }}
      >
        <DeleteIcon />
        <span className="font-['Outfit'] font-normal text-sm">Remove Photo</span>
      </div>
    </div>
  );
  return (
    <>
      <EditGroupModalFadeStyles />
      <GroupNameInputStyles />
      <CancelButtonStyles />
      <GroupImageMenuStyles />
      <CustomModal
        visible={visible}
        width="376px"
        height="545px"
        onCancel={onClose}
        wrapClassName="wa-edit-group-modal"
        body={
          <div
            className="p-4 flex flex-col h-full rounded-[12px]"
            style={{
              backgroundColor: "#F9FBFE",
              boxShadow:
                "0px 12px 16px -4px rgba(16,24,40,0.08), 0px 4px 6px -2px rgba(16,24,40,0.03)",
              height: "545px",
              gap: "16px",
            }}
          >
            <div>
              <h3 className="out-500-14 text-black !mb-0 !leading-4">Edit Group</h3>
            </div>
            <div className="flex justify-center createGroupModalV3">
              {editedGroupImage && editedGroupImage?.length > 0 ? (
                <Dropdown
                  overlay={content}
                  trigger={["click"]}
                  visible={imageMenuOpen}
                  onVisibleChange={setImageMenuOpen}
                  overlayClassName="wa-edit-group-image-menu"
                  getPopupContainer={(triggerNode) => triggerNode}
                >
                  <div className="relative group">
                    <img
                      src={editedGroupImage}
                      className="rounded-full cursor-pointer object-cover image transition-transform transform-gpu group-hover:brightness-50"
                      style={{ width: 165, height: 165 }}
                      alt="Group Icon"
                    />
                    <div className="overlay absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                      <p className="text-white text-center">Change Group Icon</p>
                    </div>
                  </div>
                </Dropdown>
              ) : (
                <ImageCropModal cropShape={"round"}>
                  <Upload
                    className="flex justify-center w-full h-[165px] "
                    action={process.env.REACT_APP_API_BASE_URL + "/v1/upload/storej"}
                    customRequest={uploadImage}
                    onChange={handleImageChange}
                    multiple={false}
                    showUploadList={false}
                  >
                    <div
                      className="rounded-full flex items-center justify-center cursor-pointer overflow-hidden border border-dashed border-gray-300"
                      style={{ width: 165, height: 165 }}
                    >
                      <span className="font-['Outfit'] font-medium text-[14px] leading-[20px] text-[#667085] text-center px-4">
                        Upload Group Icon
                      </span>
                    </div>
                  </Upload>
                </ImageCropModal>
              )}
            </div>
            <div style={{ width: "344px" }}>
              <h3 className="out-300-14 text-black" style={{ color: "#292927" }}>Group Name</h3>
              <CustomInputBox
                placeholder="e.g. Tech Team"
                value={editedGroupName}
                onChange={(e) => setEditedGroupName(e.target.value)}
                wrapperClassName="wa-group-name-input"
                style={{ width: "344px", color: "#292927" }}
              />
            </div>
            <div style={{ height: "210px" }} />
            <div className="flex justify-end">
              <CustomButton
                text="Cancel"
                className="mr-2 wa-cancel-btn"
                onClick={onClose}
              />
              <CustomButton
                text={saving ? "Saving..." : "Save Changes"}
                disabled={!(editedGroupName ?? "").trim() || saving || uploading}
                onClick={handleSave}
              />
            </div>
          </div>
        }
      />
    </>
  );
};

export default EditGroupModal;